import type { UploadTransport } from "./transport";
import { BlobAlreadyExistsError } from "./transport";
import type { FileTask, TeamForm } from "./types";

/**
 * Because the SAS cannot list the container, the manifest is how a delivered
 * data set is identified after the fact: who uploaded it, from which il, and
 * what files were meant to be in it.
 *
 * Two are written per session. "_manifest-start" goes up before any image, so
 * an abandoned session still leaves a record of who attempted it;
 * "_manifest-done" records the final per-file outcome.
 */

export interface ManifestBase {
  schema: 1;
  sessionId: string;
  kind: "start" | "done";
  createdAt: string;
  form: TeamForm;
  client: { userAgent: string; transportMode: string };
}

export interface StartManifest extends ManifestBase {
  kind: "start";
  fileCount: number;
  bytesTotal: number;
  files: Array<{ name: string; size: number; blobPath: string }>;
}

export interface DoneManifest extends ManifestBase {
  kind: "done";
  summary: { total: number; uploaded: number; alreadyPresent: number; failed: number };
  files: Array<{ name: string; size: number; blobPath: string; status: string; error?: string }>;
}

/** Without the ".json" suffix, so numbered variants can be appended. */
function manifestBase(prefix: string, form: TeamForm, sessionId: string, kind: string): string {
  return `${prefix}/${form.provinceSlug}/${sessionId}/_manifest-${kind}`;
}

async function putJson(
  transport: UploadTransport,
  blobPath: string,
  payload: unknown
): Promise<void> {
  const body = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  try {
    await transport.put({ blobPath, body, contentType: "application/json" });
  } catch (err) {
    // A resumed session re-writes the start manifest; the original is fine to keep.
    if (!(err instanceof BlobAlreadyExistsError)) throw err;
  }
}

/**
 * Writes to the first free numbered name. A session that is interrupted and
 * resumed finishes more than once, and overwrite is blocked, so each completed
 * run needs its own manifest rather than silently losing the later one.
 */
async function putJsonNumbered(
  transport: UploadTransport,
  basePath: string,
  payload: unknown
): Promise<void> {
  const body = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  for (let n = 1; n <= 20; n++) {
    const blobPath = n === 1 ? `${basePath}.json` : `${basePath}-${n}.json`;
    try {
      await transport.put({ blobPath, body, contentType: "application/json" });
      return;
    } catch (err) {
      if (!(err instanceof BlobAlreadyExistsError)) throw err;
    }
  }
}

export async function writeStartManifest(
  transport: UploadTransport,
  prefix: string,
  form: TeamForm,
  sessionId: string,
  tasks: FileTask[]
): Promise<void> {
  const payload: StartManifest = {
    schema: 1,
    sessionId,
    kind: "start",
    createdAt: new Date().toISOString(),
    form,
    client: { userAgent: navigator.userAgent, transportMode: transport.mode },
    fileCount: tasks.length,
    bytesTotal: tasks.reduce((sum, t) => sum + t.file.size, 0),
    files: tasks.map((t) => ({ name: t.file.name, size: t.file.size, blobPath: t.blobPath })),
  };
  await putJson(transport, `${manifestBase(prefix, form, sessionId, "start")}.json`, payload);
}

export async function writeDoneManifest(
  transport: UploadTransport,
  prefix: string,
  form: TeamForm,
  sessionId: string,
  tasks: FileTask[]
): Promise<void> {
  const payload: DoneManifest = {
    schema: 1,
    sessionId,
    kind: "done",
    createdAt: new Date().toISOString(),
    form,
    client: { userAgent: navigator.userAgent, transportMode: transport.mode },
    summary: {
      total: tasks.length,
      uploaded: tasks.filter((t) => t.status === "done").length,
      alreadyPresent: tasks.filter((t) => t.status === "skipped").length,
      failed: tasks.filter((t) => t.status === "failed").length,
    },
    files: tasks.map((t) => ({
      name: t.file.name,
      size: t.file.size,
      blobPath: t.blobPath,
      status: t.status,
      error: t.error,
    })),
  };
  await putJsonNumbered(transport, manifestBase(prefix, form, sessionId, "done"), payload);
}
